import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { resolveImageSources } from '../utils/emailImages';
import { Table, type Column, Button, ContactOverlay, Modal, Pagination, TableSkeleton, SearchInput, VenueModal, ColumnFilter, Tooltip } from '../components/Common';
import type { VenueFormData } from '../components/Common/venueConstants';
import { contactsAPI, venuesAPI, bandsAPI, gmailAPI } from '../api/endpoints';
import { useToastAction } from '../hooks/useToastAction';
import { useOpenRequest } from '../hooks/useOpenRequest';
import type { InboxRow, Contact, Venue } from '../types';
import { parseAddress, formatEmailAddress } from '../utils/emailAddress';
import { recencyColor, recencyLabel } from '../utils/recencyColor';
import { format } from 'date-fns';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Calendar, CheckSquare, Clock, Mail, MailOpen, MapPin, RotateCcw, Send, X } from 'lucide-react';
import clsx from 'clsx';
import { getSchedulePresets } from '../utils/schedulePresets';
import { isTypingTarget } from '../utils/keyboard';

const PAGE_SIZE = 25;
// Mirrors MAX_IMMEDIATE_BATCH in app/routers/gmail.py — the server rejects anything larger.
const MAX_IMMEDIATE_BATCH = 25;

type DirectionFilter = 'all' | 'sent' | 'received' | 'scheduled' | 'failed';

function decodeHtmlEntities(text: string): string {
  const stripped = text.replace(/<[^>]*>/g, '');
  const textarea = document.createElement('textarea');
  textarea.innerHTML = stripped;
  return textarea.value;
}

export function InboxPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { run } = useToastAction();
  const [selectedVenueContacts, setSelectedVenueContacts] = useState<Contact[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [isContactOverlayOpen, setIsContactOverlayOpen] = useState(false);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [page, setPage] = useState(1);
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('received');
  const [readFilter, setReadFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [venuePickerContact, setVenuePickerContact] = useState<Contact | null>(null);
  const [venuePickerVenues, setVenuePickerVenues] = useState<Venue[]>([]);
  const [cancelConfirm, setCancelConfirm] = useState<InboxRow | null>(null);
  const [sendNowConfirm, setSendNowConfirm] = useState<InboxRow | null>(null);
  const [rescheduleRow, setRescheduleRow] = useState<InboxRow | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('10:30');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [previewRow, setPreviewRow] = useState<InboxRow | null>(null);
  // Multi-select, offered on Scheduled and Failed: their rows have not been sent, so they
  // still have actions in common. Sent and received mail has nothing to act on in bulk.
  const [isMultiMode, setIsMultiMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<'requeue' | 'send-now' | 'cancel' | null>(null);
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const hidePreviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showPreview = useCallback((row: InboxRow) => {
    if (hidePreviewTimer.current) clearTimeout(hidePreviewTimer.current);
    setPreviewRow(row);
  }, []);

  const scheduleHidePreview = useCallback(() => {
    hidePreviewTimer.current = setTimeout(() => setPreviewRow(null), 150);
  }, []);

  const cancelHidePreview = useCallback(() => {
    if (hidePreviewTimer.current) clearTimeout(hidePreviewTimer.current);
  }, []);

  // Opening the overlay waits on the contact fetch; this retires one the user closed meanwhile
  const openRequest = useOpenRequest();

  // Overlay navigation state
  const [overlayAbsoluteIndex, setOverlayAbsoluteIndex] = useState<number | null>(null);
  const [pendingNavIndex, setPendingNavIndex] = useState<number | null>(null);
  const [isOverlayNavigating, setIsOverlayNavigating] = useState(false);
  const [overlayRow, setOverlayRow] = useState<InboxRow | null>(null);

  const isReadParam = readFilter.length === 1
    ? readFilter[0] === 'unread' ? false : true
    : undefined;

  // Server-side paginated inbox query
  const { data: inboxData, isLoading, isFetching } = useQuery({
    queryKey: ['inbox', page, directionFilter, readFilter, searchQuery],
    queryFn: async () => {
      const response = await contactsAPI.inbox({
        page,
        page_size: PAGE_SIZE,
        direction: directionFilter,
        is_read: isReadParam,
        q: searchQuery || undefined,
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
  });

  const rows = inboxData?.items ?? [];
  const total = inboxData?.total ?? 0;
  const totalPages = inboxData?.total_pages ?? 1;
  const hasNext = inboxData?.has_next ?? false;
  const hasPrev = inboxData?.has_prev ?? false;

  // Refs so handleOpenContact stays stable while reading current rows/page
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const pageRef = useRef(page);
  pageRef.current = page;

  // Fetch venues for the current page's rows (for venue name display and overlay)
  const pageVenueIds = useMemo(() => {
    const items = inboxData?.items ?? [];
    const ids = new Set<string>();
    for (const row of items) {
      for (const vid of row.venue_ids) ids.add(vid);
    }
    return [...ids];
  }, [inboxData]);

  const { data: venues = [] } = useQuery({
    queryKey: ['venues', 'inbox', pageVenueIds],
    queryFn: async () => {
      if (pageVenueIds.length === 0) return [];
      const results = await Promise.all(pageVenueIds.map(id => venuesAPI.getById(id).catch(() => null)));
      return results.filter((r): r is Awaited<ReturnType<typeof venuesAPI.getById>> => r !== null).map(r => r.data);
    },
    enabled: pageVenueIds.length > 0,
    staleTime: 60000,
  });

  const { data: bands = [] } = useQuery({
    queryKey: ['bands'],
    queryFn: async () => {
      const response = await bandsAPI.list();
      return response.data.items;
    },
    staleTime: 60000,
  });

  const venueMap = useMemo(() => {
    const map = new Map<string, Venue>();
    for (const venue of venues) {
      map.set(venue._id, venue);
    }
    return map;
  }, [venues]);

  const handleDirectionFilter = useCallback((filter: DirectionFilter) => {
    setDirectionFilter(filter);
    // Sent, scheduled and failed mail has no read state; a read filter carried over would
    // empty the tab. Leaving multi-select on across tabs would also carry a stale selection.
    if (filter !== 'received' && filter !== 'all') setReadFilter([]);
    setIsMultiMode(false);
    setSelectedIds(new Set());
    setPage(1);
  }, []);

  const handleReadFilter = useCallback((selected: string[]) => {
    setReadFilter(selected);
    setPage(1);
  }, []);

  // "u" toggles the Unread filter on the Received tab (docs/SHORTCUTS.md). It only ever turns
  // unread-only on and off — it never selects "Read" — and it is off on the other tabs, where the
  // filter itself is either hidden (sent, scheduled) or applies to a mixed list (all).
  const isAnyModalOpen = isContactOverlayOpen || isVenueModalOpen || !!venuePickerContact
    || !!cancelConfirm || !!sendNowConfirm || !!rescheduleRow;
  useEffect(() => {
    if (directionFilter !== 'received' || isAnyModalOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e)) return;
      if (e.key.toLowerCase() !== 'u') return;
      e.preventDefault();
      handleReadFilter(readFilter.length === 1 && readFilter[0] === 'unread' ? [] : ['unread']);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [directionFilter, isAnyModalOpen, readFilter, handleReadFilter]);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  // Only received mail has a read state: row.is_read is null for sent and scheduled rows, and
  // both toggles are no-ops on them.
  const markAsRead = useCallback((row: InboxRow) => {
    if (row.is_read === false) {
      if (row.gmail_message_id) {
        contactsAPI.markMessageRead(row.contact_id, row.comm_index, row.gmail_message_id).catch(() => {});
      } else {
        contactsAPI.markCommunicationRead(row.contact_id, row.comm_index).catch(() => {});
      }
      // Optimistically update the inbox query cache
      queryClient.setQueryData(['inbox', page, directionFilter, readFilter, searchQuery], (old: typeof inboxData) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((r: InboxRow) =>
            r.contact_id === row.contact_id && r.comm_index === row.comm_index &&
            r.gmail_message_id === row.gmail_message_id
              ? { ...r, is_read: true }
              : r
          ),
        };
      });
      // Keep the navbar unread badge (counts unread received mail) in sync
      if (row.direction === 'received') {
        queryClient.setQueryData(['inbox', 'unread-count'], (old: typeof inboxData) =>
          old ? { ...old, total: Math.max(0, old.total - 1) } : old
        );
      }
    }
  }, [queryClient, page, directionFilter, readFilter, searchQuery]);

  const markAsUnread = useCallback((row: InboxRow) => {
    if (row.is_read === true) {
      if (row.gmail_message_id) {
        contactsAPI.markMessageUnread(row.contact_id, row.comm_index, row.gmail_message_id).catch(() => {});
      } else {
        contactsAPI.markCommunicationUnread(row.contact_id, row.comm_index).catch(() => {});
      }
      queryClient.setQueryData(['inbox', page, directionFilter, readFilter, searchQuery], (old: typeof inboxData) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((r: InboxRow) =>
            r.contact_id === row.contact_id && r.comm_index === row.comm_index &&
            r.gmail_message_id === row.gmail_message_id
              ? { ...r, is_read: false }
              : r
          ),
        };
      });
      // Keep the navbar unread badge (counts unread received mail) in sync
      if (row.direction === 'received') {
        queryClient.setQueryData(['inbox', 'unread-count'], (old: typeof inboxData) =>
          old ? { ...old, total: old.total + 1 } : old
        );
      }
    }
  }, [queryClient, page, directionFilter, readFilter, searchQuery]);

  const handleOpenContact = useCallback(async (row: InboxRow, absoluteIndex?: number) => {
    const token = openRequest.begin();
    markAsRead(row);

    // Track which row is in the overlay and its absolute position in the paginated results.
    // During navigation the target index is passed in directly; for direct clicks we look it up.
    // The lookup must match the full row identity (incl. gmail_message_id) because a single
    // thread is exploded into multiple rows sharing the same contact_id + comm_index.
    if (absoluteIndex !== undefined) {
      setOverlayAbsoluteIndex(absoluteIndex);
    } else {
      const idxInPage = rowsRef.current.findIndex(r =>
        r.contact_id === row.contact_id &&
        r.comm_index === row.comm_index &&
        r.gmail_message_id === row.gmail_message_id
      );
      // A row that is not on the page in hand (opened from elsewhere) has no position to
      // navigate from — leaving the last one in place would send prev/next somewhere unrelated.
      setOverlayAbsoluteIndex(idxInPage === -1 ? null : (pageRef.current - 1) * PAGE_SIZE + idxInPage);
    }
    setOverlayRow(row);

    // Fetch the full contact for the overlay
    try {
      const res = await contactsAPI.getById(row.contact_id);
      const contact = res.data;
      // A contact carries its mail bodies, so this fetch can run for seconds. If the overlay was
      // closed, or another row opened, while it was in the air, this open is no longer wanted —
      // opening it here is what made a dismissed overlay reappear by itself.
      if (!openRequest.isCurrent(token)) return;

      if (row.venue_ids.length === 0) {
        setSelectedVenue(null);
        setSelectedVenueContacts([contact]);
        setIsContactOverlayOpen(true);
      } else if (row.venue_ids.length === 1) {
        const venue = venueMap.get(row.venue_ids[0]) ?? null;
        setSelectedVenue(venue);
        setSelectedVenueContacts([contact]);
        setIsContactOverlayOpen(true);
        if (venue && venue.contact_ids.length > 1) {
          contactsAPI.batchGet(venue.contact_ids).then(r => setSelectedVenueContacts(r.data)).catch(() => {});
        }
      } else {
        const matchedVenues = row.venue_ids
          .map(id => venueMap.get(id))
          .filter((v): v is Venue => v != null);
        setVenuePickerVenues(matchedVenues);
        setVenuePickerContact(contact);
      }
    } catch {
      // If fetch fails, just skip
    }
  }, [venueMap, markAsRead, openRequest]);

  const handleOverlayNavigate = useCallback((direction: 'prev' | 'next') => {
    if (overlayAbsoluteIndex === null) return;
    const target = overlayAbsoluteIndex + (direction === 'next' ? 1 : -1);
    const targetPage = Math.floor(target / PAGE_SIZE) + 1;
    const idxInPage = target % PAGE_SIZE;

    if (targetPage === pageRef.current) {
      const row = rowsRef.current[idxInPage];
      if (row) handleOpenContact(row, target);
    } else {
      setIsOverlayNavigating(true);
      setPendingNavIndex(target);
      setPage(targetPage);
    }
  }, [overlayAbsoluteIndex, handleOpenContact]);

  // Resolve cross-page navigation once new page data has loaded.
  //
  // Every exit from here has to clear `pendingNavIndex` and `isOverlayNavigating`. A pending
  // index left behind freezes the overlay's prev/next (both are gated on `isNavigating`) and
  // turns the next refetch — an email sync, a cancelled send — into a navigation nobody asked
  // for, because this effect runs again on the new rows.
  useEffect(() => {
    if (pendingNavIndex === null) return;
    const giveUp = () => {
      setPendingNavIndex(null);
      setIsOverlayNavigating(false);
    };
    // Closed while the page was loading: nothing to navigate, and nothing to reopen.
    if (!isContactOverlayOpen) {
      giveUp();
      return;
    }
    if (isFetching) return;
    const targetPage = Math.floor(pendingNavIndex / PAGE_SIZE) + 1;
    // Still the previous page's rows (`keepPreviousData`). Nothing else can move the list while
    // the overlay is open — it covers the filters and the pager — so the page is on its way.
    if (inboxData && inboxData.page !== targetPage) return;
    const row = rowsRef.current[pendingNavIndex % PAGE_SIZE];
    // No row there: the result set shrank under the open overlay (mail cancelled, a filter
    // re-run). Stay on the row in hand rather than waiting for one that is not coming.
    if (row) handleOpenContact(row, pendingNavIndex);
    giveUp();
  }, [rows, pendingNavIndex, isFetching, handleOpenContact, isContactOverlayOpen, inboxData]);

  const overlayHasPrev = overlayAbsoluteIndex !== null && overlayAbsoluteIndex > 0;
  const overlayHasNext = overlayAbsoluteIndex !== null && overlayAbsoluteIndex < total - 1;

  const openContactWithVenue = useCallback((contact: Contact, venue: Venue | null) => {
    setSelectedVenue(venue);
    setSelectedVenueContacts([contact]);
    setIsContactOverlayOpen(true);
    if (venue && venue.contact_ids.length > 1) {
      contactsAPI.batchGet(venue.contact_ids).then(r => setSelectedVenueContacts(r.data)).catch(() => {});
    }
  }, []);

  const handleVenueClick = useCallback((e: React.MouseEvent, venueId: string) => {
    e.stopPropagation();
    const venue = venueMap.get(venueId);
    if (venue) {
      navigate(`/venues?q=${encodeURIComponent(venue.name)}`);
    }
  }, [navigate, venueMap]);

  const handleCancelScheduled = useCallback(async (row: InboxRow) => {
    if (!row.scheduled_email_id) return;
    try {
      await gmailAPI.cancelScheduled(row.scheduled_email_id);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    } catch {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    }
  }, [queryClient]);

  const handleSendNow = useCallback(async (row: InboxRow) => {
    if (!row.scheduled_email_id) return;
    try {
      await gmailAPI.sendNowScheduled(row.scheduled_email_id);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    } catch {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    }
  }, [queryClient]);

  // Recomputed (fresh jitter included) only when the modal opens, so the times shown don't
  // shift under the user while it's sitting open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schedulePresets = useMemo(() => getSchedulePresets(), [rescheduleRow]);

  const handleRescheduleConfirm = useCallback(async (isoDate: string) => {
    if (!rescheduleRow?.scheduled_email_id) return;
    setIsRescheduling(true);
    try {
      await gmailAPI.rescheduleScheduled(rescheduleRow.scheduled_email_id, isoDate);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    } catch {
      // silent
    } finally {
      setIsRescheduling(false);
      setRescheduleRow(null);
    }
  }, [rescheduleRow, queryClient]);

  // The two bulk actions, deliberately different animals. Re-queue hands the selection back to
  // the dispatcher, which lays it out under each batch's send window, spacing and daily max.
  // Send immediately bypasses all of that, so it is capped server-side and asks first.
  const handleBulkRequeue = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setIsBulkRunning(true);
    run(
      () => gmailAPI.batchRequeueScheduled(ids).then((res) => {
        setSelectedIds(new Set());
        setBulkConfirm(null);
        queryClient.invalidateQueries({ queryKey: ['inbox'] });
        return res;
      }).finally(() => setIsBulkRunning(false)),
      {
        loading: `Re-queueing ${ids.length} email${ids.length === 1 ? '' : 's'}...`,
        success: `${ids.length} email${ids.length === 1 ? '' : 's'} back in the queue`,
        error: 'Could not re-queue those emails',
      },
    );
  }, [selectedIds, run, queryClient]);

  const handleBulkSendNow = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setIsBulkRunning(true);
    run(
      () => gmailAPI.batchSendNowScheduled(ids).then((res) => {
        setSelectedIds(new Set());
        setBulkConfirm(null);
        queryClient.invalidateQueries({ queryKey: ['inbox'] });
        return res;
      }).finally(() => setIsBulkRunning(false)),
      {
        loading: `Sending ${ids.length} email${ids.length === 1 ? '' : 's'}...`,
        success: 'Sent',
        error: 'Could not send those emails',
      },
    );
  }, [selectedIds, run, queryClient]);

  const handleBulkCancel = useCallback(() => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setIsBulkRunning(true);
    run(
      () => gmailAPI.batchCancelScheduled(ids).then((res) => {
        setSelectedIds(new Set());
        setBulkConfirm(null);
        queryClient.invalidateQueries({ queryKey: ['inbox'] });
        return res;
      }).finally(() => setIsBulkRunning(false)),
      {
        loading: `Cancelling ${ids.length} email${ids.length === 1 ? '' : 's'}...`,
        success: `${ids.length} email${ids.length === 1 ? '' : 's'} cancelled`,
        error: 'Could not cancel those emails',
      },
    );
  }, [selectedIds, run, queryClient]);

  const handleToggleMultiMode = useCallback(() => {
    setIsMultiMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  const handleToggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleTogglePage = useCallback((pageIds: string[], allSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // Escape leaves multi mode, as on the venues table.
  useEffect(() => {
    if (!isMultiMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMultiMode(false);
        setSelectedIds(new Set());
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMultiMode]);

  // A selection names rows on the page it was made on; changing page would act on ids the user
  // can no longer see. Unlike the venues table there is no "select all matching" here, so the
  // selection is simply dropped.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, searchQuery]);

  // The tabs whose rows can be acted on in bulk. One definition, so the Multi button, the
  // action bar and the table's checkboxes cannot drift apart.
  const supportsMulti = directionFilter === 'failed' || directionFilter === 'scheduled';

  const selectionConfig = useMemo(() => ({
    getRowId: (row: InboxRow) => row.scheduled_email_id ?? `${row.contact_id}:${row.comm_index}`,
    selectedIds,
    onToggleRow: handleToggleRow,
    onTogglePage: handleTogglePage,
  }), [selectedIds, handleToggleRow, handleTogglePage]);

  const handleRescheduleCustom = useCallback(() => {
    if (!rescheduleDate || !rescheduleTime) return;
    const dt = new Date(`${rescheduleDate}T${rescheduleTime}:00`);
    handleRescheduleConfirm(dt.toISOString());
  }, [rescheduleDate, rescheduleTime, handleRescheduleConfirm]);

  const handleEditVenue = useCallback((venue: Venue) => {
    setEditingVenue(venue);
    setIsVenueModalOpen(true);
  }, []);

  const handleVenueSubmit = useCallback((venueData: VenueFormData): void => {
    if (!editingVenue) return;
    const id = editingVenue._id;
    run(
      () => venuesAPI.update(id, venueData).then((res) => {
        // Keep the open contact overlay in sync with the edited venue
        setSelectedVenue(prev => (prev && prev._id === id ? res.data : prev));
        return queryClient.invalidateQueries({ queryKey: ['venues'] });
      }),
      { loading: 'Updating venue...', success: 'Venue updated' },
    );
    setIsVenueModalOpen(false);
    setEditingVenue(null);
  }, [editingVenue, run, queryClient]);

  const handleCloseVenueModal = useCallback(() => {
    setIsVenueModalOpen(false);
    setEditingVenue(null);
  }, []);

  // Resolve venue names for each row
  const getVenueNames = useCallback((venueIds: string[]) => {
    return venueIds
      .map(id => venueMap.get(id))
      .filter((v): v is Venue => v != null)
      .map(v => v.name);
  }, [venueMap]);

  const columns: Column<InboxRow>[] = [
    {
      key: 'contact',
      label: 'Contact',
      sortable: true,
      width: '180px',
      render: (row) => {
        // A received message is not necessarily from the contact that owns the thread:
        // mail to a shared inbox is often answered from a personal mailbox at the same
        // domain. Label the row with the real sender and keep the contact as "via".
        const sender = row.direction === 'received' ? parseAddress(row.from_address) : null;
        const senderIsContact = !sender || sender.email === row.contact_email.toLowerCase();
        const primary = senderIsContact
          ? (row.contact_name || row.contact_email || 'Unknown')
          : formatEmailAddress(sender);
        const secondary = senderIsContact
          ? (row.contact_name && row.contact_email ? row.contact_email : null)
          : (sender.name ? sender.email : null);
        return (
          <div>
            <div className={row.is_read === false ? 'font-medium' : 'text-dark-text-secondary'}>
              <span className={`text-xs mr-1 ${row.direction === 'scheduled' ? 'text-yellow-400' : row.direction === 'sent' ? 'text-primary-400' : 'text-green-400'}`}>
                {row.direction === 'scheduled' ? 'To:' : row.direction === 'sent' ? 'To:' : 'From:'}
              </span>
              {primary}
            </div>
            {secondary && <div className="text-xs text-dark-text-muted truncate">{secondary}</div>}
            {!senderIsContact && (
              <div
                className="text-xs text-dark-text-muted italic truncate"
                title={`Stored on contact ${row.contact_name || row.contact_email}`}
              >
                via {row.contact_name || row.contact_email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      render: (row) => (
        <div
          onMouseEnter={() => showPreview(row)}
          onMouseLeave={scheduleHidePreview}
        >
          <div className={`mb-0.5 ${row.is_read === false ? 'font-medium' : 'text-dark-text-secondary'}`}>
            {row.subject}
          </div>
          <div className="text-sm text-dark-text-muted truncate max-w-md">{decodeHtmlEntities(row.preview)}</div>
        </div>
      ),
    },
    {
      key: 'venue',
      label: 'Venue',
      width: '150px',
      render: (row) => {
        const venueNames = getVenueNames(row.venue_ids);
        if (venueNames.length === 0) {
          return <span className="text-dark-text-muted">&mdash;</span>;
        }
        return (
          <div className="flex items-center gap-1 flex-wrap">
            <span
              className="text-primary-400 hover:text-primary-300 hover:underline cursor-pointer text-sm"
              onClick={(e) => handleVenueClick(e, row.venue_ids[0])}
            >
              {venueNames[0]}
            </span>
            {venueNames.length > 1 && (
              <span className="text-xs text-dark-text-muted">+{venueNames.length - 1}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      width: '170px',
      render: (row) => {
        if (!row.date) return <span className="text-dark-text-muted">&mdash;</span>;
        const d = new Date(row.date);
        if (row.direction === 'scheduled') {
          return (
            <Tooltip content={recencyLabel(d)}>
              <div className="text-sm">
                <div className="flex items-center gap-1 text-yellow-400">
                  <Clock className="w-3 h-3" />
                  <span>{format(d, 'MMM d, yyyy')}</span>
                </div>
                <div className="text-xs text-yellow-400/70">{format(d, 'h:mm a')}</div>
              </div>
            </Tooltip>
          );
        }
        return (
          <Tooltip content={recencyLabel(d)}>
            <div className="text-sm" style={{ color: recencyColor(d) }}>
              {format(d, 'MMM d, yyyy')}
              <div className="text-xs opacity-70">{format(d, 'h:mm a')}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '150px',
      // Sent and scheduled mail has no read state, so the Read/Unread filter has nothing to say
      // on those tabs (and on "All" it narrows to received mail on its own, server-side).
      filterable: directionFilter !== 'sent' && directionFilter !== 'scheduled',
      renderFilter: () => (
        <ColumnFilter
          values={[
            { value: 'unread', label: 'Unread' },
            { value: 'read', label: 'Read' },
          ]}
          selectedValues={readFilter}
          onChange={handleReadFilter}
          alwaysVisible
        />
      ),
      render: (row) => row.direction === 'scheduled' || row.direction === 'failed' ? (
        <div className="flex items-center gap-0.5">
          <Button
            size="sm"
            variant="ghost"
            title={row.direction === 'failed' ? 'Re-queue at a chosen time' : 'Reschedule'}
            onClick={(e) => {
              e.stopPropagation();
              setRescheduleRow(row);
              setRescheduleDate('');
              setRescheduleTime('10:30');
            }}
            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20"
          >
            <Calendar className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={row.direction === 'failed' ? 'Retry now' : 'Send now'}
            onClick={(e) => {
              e.stopPropagation();
              setSendNowConfirm(row);
            }}
            className="text-green-400 hover:text-green-300 hover:bg-green-900/20"
          >
            <Send className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title={row.direction === 'failed' ? 'Dismiss this failed email' : 'Cancel scheduled email'}
            onClick={(e) => {
              e.stopPropagation();
              setCancelConfirm(row);
            }}
            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : row.is_read === null ? null : (
        <Button
          size="sm"
          variant="ghost"
          title={row.is_read ? 'Mark as unread' : 'Mark as read'}
          onClick={(e) => {
            e.stopPropagation();
            if (row.is_read) { markAsUnread(row); } else { markAsRead(row); }
          }}
        >
          {row.is_read ? <MailOpen className="w-4 h-4 text-dark-text-muted" /> : <Mail className="w-4 h-4" />}
        </Button>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-2xl) mx-auto w-full p-3 sm:p-4 md:p-6 flex-1 flex flex-col">
        <div className="mb-6 animate-slideDown flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-dark-text-primary mb-2 tracking-tight flex items-center gap-2">
              <Mail className="w-6 h-6" />
              Inbox
            </h1>
            <p className="text-sm text-dark-text-secondary">
              All your communications in one place
            </p>
          </div>
          <div className="flex items-center gap-2 animate-scaleIn">
            {supportsMulti && (
              <button
                onClick={handleToggleMultiMode}
                title={isMultiMode ? 'Exit multi-select mode' : 'Enable multi-select mode'}
                className={clsx(
                  'px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors flex items-center gap-1.5',
                  isMultiMode
                    ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                    : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
                )}
              >
                <CheckSquare className="w-4 h-4" />
                Multi
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 card animate-scaleIn flex flex-col">
          {/* Direction filter tabs */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {([['all', 'All'], ['received', 'Received'], ['sent', 'Sent'], ['scheduled', 'Scheduled'], ['failed', 'Failed']] as const).map(([value, label]) => (
              <button
                key={value}
                onClick={() => handleDirectionFilter(value)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                  directionFilter === value
                    ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                    : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                }`}
              >
                {value === 'received' && <ArrowDownLeft className="w-3.5 h-3.5" />}
                {value === 'sent' && <ArrowUpRight className="w-3.5 h-3.5" />}
                {value === 'scheduled' && <Clock className="w-3.5 h-3.5" />}
                {value === 'failed' && <AlertTriangle className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>

          {/* Multi-select action bar */}
          {isMultiMode && supportsMulti && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2 bg-primary-900/20 border border-primary-700/40 rounded-lg text-sm">
              <span className={clsx('font-medium', selectedIds.size > 0 ? 'text-primary-300' : 'text-dark-text-muted')}>
                {selectedIds.size > 0
                  ? `${selectedIds.size} ${selectedIds.size === 1 ? 'email' : 'emails'} selected`
                  : 'No emails selected'}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  disabled={selectedIds.size === 0 || isBulkRunning}
                  onClick={() => setBulkConfirm('requeue')}
                  title={directionFilter === 'failed'
                    ? 'Put these back in the queue, spaced by their normal send rules'
                    : 'Lay these out again from now, spaced by their normal send rules'}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedIds.size > 0 && !isBulkRunning
                      ? 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {directionFilter === 'failed' ? 'Re-queue' : 'Reschedule'}
                </button>
                <button
                  disabled={selectedIds.size === 0 || isBulkRunning}
                  onClick={() => setBulkConfirm('send-now')}
                  title={`Send right now, ignoring the send window (max ${MAX_IMMEDIATE_BATCH})`}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedIds.size > 0 && !isBulkRunning
                      ? 'border-amber-700/50 bg-amber-900/20 text-amber-300 hover:bg-amber-900/30'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                >
                  <Send className="w-3.5 h-3.5" />
                  Send immediately
                </button>
                <button
                  disabled={selectedIds.size === 0 || isBulkRunning}
                  onClick={() => setBulkConfirm('cancel')}
                  title={directionFilter === 'failed'
                    ? 'Dismiss these failed emails'
                    : 'Cancel these scheduled emails'}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors text-xs font-medium',
                    selectedIds.size > 0 && !isBulkRunning
                      ? 'border-red-800/50 bg-red-900/20 text-red-300 hover:bg-red-900/30'
                      : 'border-dark-border bg-dark-elevated text-dark-text-muted opacity-40 cursor-not-allowed'
                  )}
                >
                  <X className="w-3.5 h-3.5" />
                  {directionFilter === 'failed' ? 'Dismiss' : 'Cancel'}
                </button>
              </div>
            </div>
          )}

          {/* Search and top pagination */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 items-center">
            <SearchInput
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search contacts, subjects..."
              className="flex-1 max-w-md"
            />
            {totalPages > 0 && (
              <div className="ml-auto">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  pageSize={PAGE_SIZE}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  onPageChange={setPage}
                  isFetching={isFetching}
                  compact
                />
              </div>
            )}
          </div>
          {isLoading ? (
            <TableSkeleton rows={8} />
          ) : (
            <>
              <div className="flex-1">
                <Table
                  data={rows}
                  columns={columns}
                  searchable={false}
                  emptyMessage="No communications found"
                  onRowClick={isMultiMode ? undefined : (row) => handleOpenContact(row)}
                  selection={isMultiMode ? selectionConfig : undefined}
                  rowClassName={(row) => {
                    const highlight = isContactOverlayOpen && overlayRow !== null
                      && row.contact_id === overlayRow.contact_id && row.comm_index === overlayRow.comm_index
                      ? 'ring-1 ring-inset ring-primary-500'
                      : '';
                    if (row.direction === 'failed') return `bg-red-900/15 ${highlight}`.trim();
                    if (row.direction === 'scheduled') return `bg-yellow-900/15 ${highlight}`.trim();
                    if (row.direction === 'received') return `bg-green-900/15 ${highlight}`.trim();
                    return `bg-dark-elevated ${highlight}`.trim();
                  }}
                />
              </div>
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={PAGE_SIZE}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onPageChange={setPage}
                isFetching={isFetching}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={venuePickerContact !== null}
        onClose={() => {
          setVenuePickerContact(null);
          setVenuePickerVenues([]);
        }}
        title="Select Venue"
        size="sm"
      >
        <div className="space-y-2 p-2">
          <p className="text-sm text-dark-text-secondary mb-3">
            This contact is linked to multiple venues. Select one to continue:
          </p>
          {venuePickerVenues.map((venue) => (
            <button
              key={venue._id}
              className="w-full text-left px-4 py-3 rounded-lg bg-dark-bg hover:bg-dark-border transition-colors flex items-center gap-3"
              onClick={() => {
                const contact = venuePickerContact;
                setVenuePickerContact(null);
                setVenuePickerVenues([]);
                if (contact) {
                  openContactWithVenue(contact, venue);
                }
              }}
            >
              <MapPin className="w-4 h-4 text-primary-400 shrink-0" />
              <div>
                <div className="font-medium text-dark-text-primary">{venue.name}</div>
                {venue.address?.city && (
                  <div className="text-xs text-dark-text-muted">{venue.address.city}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </Modal>

      <ContactOverlay
        isOpen={isContactOverlayOpen}
        onClose={() => {
          openRequest.cancel();
          setIsContactOverlayOpen(false);
          setSelectedVenueContacts([]);
          setSelectedVenue(null);
          setOverlayRow(null);
          setOverlayAbsoluteIndex(null);
          setPendingNavIndex(null);
          setIsOverlayNavigating(false);
        }}
        venue={selectedVenue}
        contacts={selectedVenueContacts}
        bands={bands}
        hasPrev={overlayHasPrev}
        hasNext={overlayHasNext}
        isNavigating={isOverlayNavigating}
        onNavigate={handleOverlayNavigate}
        onEditVenue={handleEditVenue}
      />

      <VenueModal
        isOpen={isVenueModalOpen}
        onClose={handleCloseVenueModal}
        onSubmit={handleVenueSubmit}
        venue={editingVenue}
        mode="edit"
      />

      {/* Bulk confirm. Re-queue is routine; sending immediately bypasses the pacing that keeps
          this account's mail deliverable, so it spells out what it is about to do. */}
      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBulkConfirm(null)}>
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-[28rem]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              {bulkConfirm === 'requeue'
                ? (directionFilter === 'failed' ? 'Re-queue these emails?' : 'Reschedule these emails?')
                : bulkConfirm === 'cancel'
                  ? (directionFilter === 'failed' ? 'Dismiss these failed emails?' : 'Cancel these scheduled emails?')
                  : 'Send these emails immediately?'}
            </h3>
            {bulkConfirm === 'requeue' ? (
              <p className="text-xs text-dark-text-secondary mb-4">
                {selectedIds.size} email{selectedIds.size === 1 ? '' : 's'} will be laid out again
                from now, spread under their normal send window, spacing and daily maximum. Nothing
                is sent right now — the first may be days away if the window is closed.
              </p>
            ) : bulkConfirm === 'cancel' ? (
              <p className="text-xs text-dark-text-secondary mb-4">
                {selectedIds.size} email{selectedIds.size === 1 ? '' : 's'} will be cancelled and
                removed from their contacts&rsquo; history. Nothing that has already been sent is
                affected. This cannot be undone from here.
              </p>
            ) : (
              <>
                <p className="text-xs text-dark-text-secondary mb-2">
                  {selectedIds.size} email{selectedIds.size === 1 ? '' : 's'} will be sent now, back
                  to back, <strong className="text-amber-300">ignoring the send window and the
                  spacing between sends</strong>.
                </p>
                <p className="text-xs text-dark-text-muted mb-4">
                  That pacing is what keeps this account's cold outreach deliverable. Prefer
                  Re-queue unless you need these few out straight away.
                </p>
                {selectedIds.size > MAX_IMMEDIATE_BATCH && (
                  <p className="text-xs text-red-400 mb-4">
                    Too many: at most {MAX_IMMEDIATE_BATCH} at a time. Use Re-queue instead.
                  </p>
                )}
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setBulkConfirm(null)}>Cancel</Button>
              <Button
                size="sm"
                variant={bulkConfirm === 'requeue' ? 'primary' : 'danger'}
                disabled={isBulkRunning || (bulkConfirm === 'send-now' && selectedIds.size > MAX_IMMEDIATE_BATCH)}
                onClick={
                  bulkConfirm === 'requeue'
                    ? handleBulkRequeue
                    : bulkConfirm === 'cancel'
                      ? handleBulkCancel
                      : handleBulkSendNow
                }
              >
                {bulkConfirm === 'requeue'
                  ? (directionFilter === 'failed' ? 'Re-queue' : 'Reschedule')
                  : bulkConfirm === 'cancel'
                    ? (directionFilter === 'failed' ? 'Dismiss' : 'Cancel emails')
                    : 'Send now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rescheduleRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRescheduleRow(null)}>
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-dark-text-primary mb-3">Reschedule Email</h3>
            <div className="py-1 mb-2">
              {schedulePresets.map((preset) => (
                <button
                  key={preset.value}
                  disabled={isRescheduling}
                  onClick={() => handleRescheduleConfirm(preset.value)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-dark-elevated transition-colors rounded-sm disabled:opacity-50"
                >
                  <span className="text-dark-text-primary">{preset.label}</span>
                  <span className="text-dark-text-tertiary">{preset.sublabel}</span>
                </button>
              ))}
            </div>
            <div className="border-t border-dark-border pt-3 space-y-2">
              <p className="text-xs font-medium text-dark-text-secondary px-1">Custom</p>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  className="flex-1 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                />
                <input
                  type="time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="w-24 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                />
              </div>
              <Button
                size="sm"
                variant="primary"
                className="w-full"
                disabled={!rescheduleDate || !rescheduleTime || isRescheduling}
                onClick={handleRescheduleCustom}
              >
                <Calendar className="w-3 h-3" />
                Reschedule for {rescheduleDate && rescheduleTime
                  ? format(new Date(`${rescheduleDate}T${rescheduleTime}`), 'MMM d, HH:mm')
                  : '...'}
              </Button>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setRescheduleRow(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {sendNowConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-full max-w-sm mx-4">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              Send Email?
            </h3>
            <p className="text-xs text-dark-text-secondary mb-1">
              <span className="font-medium">To:</span> {sendNowConfirm.contact_email}
            </p>
            <p className="text-xs text-dark-text-secondary mb-4">
              <span className="font-medium">Subject:</span> {sendNowConfirm.subject}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setSendNowConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  handleSendNow(sendNowConfirm);
                  setSendNowConfirm(null);
                }}
              >
                <Send className="w-3 h-3" />
                Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {cancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-full max-w-sm mx-4">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              Cancel Scheduled Email?
            </h3>
            <p className="text-xs text-dark-text-secondary mb-4">
              This will cancel the scheduled email. It will not be sent. Are you sure?
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setCancelConfirm(null)}>
                Keep
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  handleCancelScheduled(cancelConfirm);
                  setCancelConfirm(null);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Cancel Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {previewRow && (
        <div
          className="fixed top-16 md:top-20 right-0 h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] w-[420px] bg-dark-surface border-l border-dark-border shadow-2xl z-50 flex flex-col"
          onMouseEnter={cancelHidePreview}
          onMouseLeave={scheduleHidePreview}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-dark-border shrink-0">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-dark-text-primary leading-snug mb-2">
                {previewRow.subject}
              </h2>
              <div className="space-y-1">
                {(() => {
                  // Same shared-inbox caveat as the Contact column: prefer the message's
                  // own From header over the contact that happens to own the thread.
                  const sender = previewRow.direction === 'received' ? parseAddress(previewRow.from_address) : null;
                  const senderIsContact = !sender || sender.email === previewRow.contact_email.toLowerCase();
                  return (
                    <>
                      <div className="flex items-baseline gap-1.5 text-xs">
                        <span className={`font-medium shrink-0 ${previewRow.direction === 'received' ? 'text-green-400' : previewRow.direction === 'scheduled' ? 'text-yellow-400' : 'text-primary-400'}`}>
                          {previewRow.direction === 'received' ? 'From:' : 'To:'}
                        </span>
                        <span className="text-dark-text-secondary truncate">
                          {senderIsContact ? (
                            previewRow.contact_name
                              ? <>{previewRow.contact_name}{previewRow.contact_email && <span className="text-dark-text-muted ml-1">&lt;{previewRow.contact_email}&gt;</span>}</>
                              : previewRow.contact_email
                          ) : (
                            sender.name
                              ? <>{sender.name}<span className="text-dark-text-muted ml-1">&lt;{sender.email}&gt;</span></>
                              : sender.email
                          )}
                        </span>
                      </div>
                      {!senderIsContact && (
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="font-medium shrink-0 text-dark-text-muted">Contact:</span>
                          <span className="text-dark-text-muted truncate">
                            {previewRow.contact_name || previewRow.contact_email}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()}
                {previewRow.date && (
                  <div className="flex items-baseline gap-1.5 text-xs">
                    <span className="font-medium shrink-0 text-dark-text-muted">Date:</span>
                    <span className="text-dark-text-muted">{format(new Date(previewRow.date), 'MMM d, yyyy · h:mm a')}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              className="shrink-0 text-dark-text-muted hover:text-dark-text-primary transition-colors"
              onClick={() => setPreviewRow(null)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {previewRow.body ? (
              /^<[a-zA-Z]/.test(previewRow.body.trim()) ? (
                <div
                  className="email-html-content email-html-content--strip-colors text-sm text-dark-text-primary"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolveImageSources(previewRow.body), {
                    USE_PROFILES: { html: true },
                    // cid: is an email-client-only scheme; browsers can't load it. Our own
                    // library references are rewritten to blob: above, so a sent, scheduled or
                    // failed row shows the image it carries (docs/EMAIL_IMAGES.md); a cid: left
                    // over from received mail still cannot load, as before.
                    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|sms|callto|xmpp|blob):|[^a-z]|[a-z+\-]+(?:[^a-z+\-:]|$))/i,
                  }) }}
                />
              ) : (
                <pre className="text-sm text-dark-text-primary whitespace-pre-wrap font-sans leading-relaxed">{previewRow.body}</pre>
              )
            ) : (
              <p className="text-sm text-dark-text-muted italic">{previewRow.preview || 'No content available'}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
